import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable, of } from 'rxjs'
import { finalize, shareReplay, tap } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import { FuseqHeaders } from '../../FuseQ/fuseq.headers'

export type SiteTimelineEventStatus = 'success' | 'info' | 'warning' | 'error'

export interface SiteTimelineEvent {
  id: string
  source: string
  area: string
  type: string | null
  title: string
  description: string | null
  status: SiteTimelineEventStatus
  date: string
  link: string | null
  meta: Record<string, any>
}

export interface SiteTimelineSite {
  fraz: string | null
  id_anagrafica: number | null
  denominazione?: string | null
  comune?: string | null
  sigla_provincia?: string | null
  filiale?: string | null
  polo?: string | null
  fortinetId?: number | null
  ciscoId?: number | null
}

export interface SiteTimelineResponse {
  site: SiteTimelineSite
  mode: 'all' | 'operational'
  events: SiteTimelineEvent[]
  warnings: { source: string, message: string }[]
  generatedAt: string
}

export interface SiteTunnel {
  name: string
  status: string
}

export interface SiteTimelineEventDetail {
  id: string
  type: string | null
  table: string | null
  row: Record<string, any> | null
}

export interface SiteLiveTunnels {
  fraz: string
  live: boolean
  checkedAt: string
  source: 'fortinet' | 'cisco' | null
  tunnels: SiteTunnel[]
  allUp: boolean
  error: string | null
}

export interface SiteOverview {
  site: SiteTimelineSite & { regione?: string | null, sdwan?: any, dataMigrazioneSdwan?: string | null }
  sdwan: {
    fortinet: any
    cisco: any
    collaudoFortinet: any
    collaudoCisco: any
  }
  rtu: {
    row: any
    lastPing: any
    uptime30: any
  }
  speedtest: any
  tickets: {
    canManage: boolean
    counts: Record<string, number>
    mineCounts: Record<string, number>
    list: any[]
  }
  nrd: any
  voip: any
  metafield: { id: number, code: string, fields: Record<string, any> } | null
  nrdCeasedLines?: any[]
  warnings: { name: string, message: string }[]
  generatedAt: string
}

@Injectable({ providedIn: 'root' })
export class SiteTimelineApiService {
  private readonly http = inject(HttpClient)
  private readonly fuseqHeaders = inject(FuseqHeaders)
  private readonly timelineCache = new Map<string, { at: number, response: SiteTimelineResponse }>()
  private readonly timelineInFlight = new Map<string, Observable<SiteTimelineResponse>>()
  private readonly timelineCacheTtlMs = 60000

  /**
   * La timeline e' pesante (molte query per fonte). Una cache breve e la
   * condivisione delle richieste in volo evitano di martellare il backend
   * quando l'utente cambia tab o riapre la scheda sede.
   */
  getTimeline (
    params: { fraz?: string, idAnagrafica?: number, limit?: number },
    force = false
  ): Observable<SiteTimelineResponse> {
    const limit = params.limit || 50
    const key = `${params.fraz || ''}|${params.idAnagrafica || ''}|${limit}`

    if (!force) {
      const cached = this.timelineCache.get(key)
      if (cached && Date.now() - cached.at < this.timelineCacheTtlMs) {
        return of(cached.response)
      }
    }
    const inFlight = this.timelineInFlight.get(key)
    if (inFlight) {
      return inFlight
    }

    let httpParams = new HttpParams().set('limit', String(limit))
    if (params.fraz) {
      httpParams = httpParams.set('fraz', params.fraz)
    }
    if (params.idAnagrafica) {
      httpParams = httpParams.set('idAnagrafica', String(params.idAnagrafica))
    }

    const request$ = this.http.get<SiteTimelineResponse>(`${environment.API_URL}/siteTimeline`, {
      ...this.fuseqHeaders.setTokenHeaders('GET'),
      params: httpParams
    }).pipe(
      tap((response) => {
        this.timelineCache.set(key, { at: Date.now(), response })
        if (this.timelineCache.size > 20) {
          const oldest = this.timelineCache.keys().next().value
          if (oldest) {
            this.timelineCache.delete(oldest)
          }
        }
      }),
      finalize(() => this.timelineInFlight.delete(key)),
      // refCount false: se l'utente cambia tab mentre la richiesta e' in volo,
      // la richiesta completa comunque e popola la cache per il ritorno.
      shareReplay({ bufferSize: 1, refCount: false })
    )
    this.timelineInFlight.set(key, request$)
    return request$
  }

  /** Dettaglio della singola riga sorgente di un evento della timeline. */
  getEventDetail (id: string): Observable<SiteTimelineEventDetail> {
    const params = new HttpParams().set('id', id)
    return this.http.get<SiteTimelineEventDetail>(`${environment.API_URL}/siteTimeline/event`, {
      ...this.fuseqHeaders.setTokenHeaders('GET'),
      params
    })
  }

  getOverview (fraz: string): Observable<SiteOverview> {
    const params = new HttpParams().set('fraz', fraz)
    return this.http.get<SiteOverview>(`${environment.API_URL}/siteOverview`, {
      ...this.fuseqHeaders.setTokenHeaders('GET'),
      params
    })
  }

  /** Stato tunnel live: interroga l'apparato (stessa logica del check pianificazione). */
  getLiveTunnels (fraz: string): Observable<SiteLiveTunnels> {
    const params = new HttpParams().set('fraz', fraz)
    return this.http.get<SiteLiveTunnels>(`${environment.API_URL}/siteOverview/tunnels`, {
      ...this.fuseqHeaders.setTokenHeaders('GET'),
      params
    })
  }
}
